import {
  convertSeedRate,
  normalizeSeedUnit,
  type SeedAssignmentsResponse,
  type SeedAutoAssignmentResponse,
  type SeedProductAssignment,
  type SeedTrial,
  type SavedSeedProductAssignment,
} from "./seedAssignments";
import {
  type SeedProduct,
  type SeedStripe,
  type StripeAssignments,
} from "../utils/seedFlow";
import { getSeedPolygonCoordinates } from "../utils/seedGeometry";
import {
  calculateStripSeedQuantity,
  getNonEmptySeedColor,
  getStripSeedColors,
  getStripSeedReplicantMultiplier,
} from "../utils/stripSeedPresentation";

export const STRIP_STANDARD_OWNER = "field-standard";

export interface StripDesignEntry {
  strip_id: number;
  area: number;
}

export interface StripSeedTrial extends SeedTrial {
  strip_design: {
    is_strip: true;
    strips: StripDesignEntry[];
  };
}

export interface StripStandardProduct {
  id: string;
  name: string;
}

export interface StripIdentity {
  stripe: SeedStripe;
  plotGroupId: number;
}

interface StripDesignCandidate {
  is_strip?: boolean;
  strips?: StripDesignEntry[];
}

export const legacyStripOwnerId = (
  variety: string,
  measureUnit: string
): string => `legacy:${JSON.stringify([variety, measureUnit])}`;

export const validateStripSeedTrial = (trial: SeedTrial): StripSeedTrial => {
  const design = trial.strip_design as StripDesignCandidate | null | undefined;

  if (
    design?.is_strip !== true ||
    !Array.isArray(design.strips) ||
    design.strips.length === 0
  ) {
    throw new Error("The trial does not have a saved strip design.");
  }

  const groups = trial.trial_plots;
  const strips = design.strips;

  if (!Array.isArray(groups) || groups.length !== strips.length) {
    throw new Error(
      "The saved design must contain one plot group per strip. " +
        "Migrate the strip schema before assigning products."
    );
  }

  groups.forEach((group, index) => {
    if (group.plot?.is_strip !== true) {
      throw new Error(
        `Strip ${index + 1} is missing plot.is_strip. ` +
          "Correct the saved strip design before assigning seeds."
      );
    }

    getSeedPolygonCoordinates(group.plot.geojson?.geometry);

    const area = strips[index]?.area;

    if (typeof area !== "number" || !Number.isFinite(area) || area <= 0) {
      throw new Error(`Strip ${index + 1} has an invalid saved area.`);
    }
  });

  const normalized = trial.plot_property_assignments;

  if (
    normalized === undefined ||
    normalized === null ||
    !Object.prototype.hasOwnProperty.call(
      normalized,
      "seed_product_assignments"
    )
  ) {
    throw new Error(
      "The trial response is missing normalized seed assignments."
    );
  }

  if (
    normalized.seed_product_assignments !== null &&
    !Array.isArray(normalized.seed_product_assignments)
  ) {
    throw new Error("The trial returned invalid seed assignments.");
  }

  return trial as StripSeedTrial;
};

export const getStripIdentities = (
  trial: StripSeedTrial,
  stripes: SeedStripe[]
): StripIdentity[] => {
  if (
    stripes.length !== trial.strip_design.strips.length ||
    new Set(stripes.map((stripe) => stripe.id)).size !== stripes.length
  ) {
    throw new Error(
      "The displayed strips do not match the saved design. Reload the trial."
    );
  }

  const identities: StripIdentity[] = stripes.map((stripe, index) => ({
    stripe,
    plotGroupId: stripe.plotGroupId ?? index,
  }));

  const seen = new Set<number>();

  identities.forEach(({ plotGroupId }) => {
    if (
      !Number.isInteger(plotGroupId) ||
      plotGroupId < 0 ||
      plotGroupId >= trial.strip_design.strips.length ||
      seen.has(plotGroupId)
    ) {
      throw new Error("The displayed strips have invalid saved references.");
    }

    seen.add(plotGroupId);
  });

  return identities;
};

export const hydrateStripSeedAssignments = (
  trial: StripSeedTrial,
  stripes: SeedStripe[],
  unit: string
): StripeAssignments => {
  const identities = getStripIdentities(trial, stripes);
  const byGroup = new Map<number, SeedStripe>();

  identities.forEach(({ stripe, plotGroupId }) => {
    byGroup.set(plotGroupId, stripe);
  });

  const result: StripeAssignments = {};
  let standardId: string | null | undefined;

  const products =
    trial.plot_property_assignments?.seed_product_assignments ?? [];

  products.forEach((product) => {
    if (product.is_standard) {
      if (standardId !== undefined && standardId !== product.product_id) {
        throw new Error(
          "This editor supports one standard seed product per trial."
        );
      }

      standardId = product.product_id;
    }

    const ownerId = product.is_standard
      ? STRIP_STANDARD_OWNER
      : product.product_id ??
        legacyStripOwnerId(product.variety, product.measure_unit);

    product.assignments.forEach((assignment) => {
      const stripe = byGroup.get(assignment.plot_group_id);

      if (
        !Number.isInteger(assignment.plot_group_id) ||
        assignment.plot_group_id < 0 ||
        assignment.plot_cell_id !== 0 ||
        stripe === undefined
      ) {
        throw new Error("The API returned an invalid strip reference.");
      }

      if (Object.prototype.hasOwnProperty.call(result, stripe.id)) {
        throw new Error("The API returned duplicate strip assignments.");
      }

      if (!Number.isFinite(assignment.rate) || assignment.rate < 0) {
        throw new Error("The API returned an invalid seed rate.");
      }

      result[stripe.id] = {
        productId: ownerId,
        rate: convertSeedRate(assignment.rate, product.measure_unit, unit),
        color: assignment.color ?? "",
        legacy: product.product_id === null,
      };
    });
  });

  return result;
};

/**
 * The auto API returns plot-nested suggestions, not normalized assignments.
 * Convert only its one-entry-per-strip seed properties.
 *
 * Never use entry.plot_id: it is an export label.
 */
export const hydrateStripSeedSuggestions = (
  trial: StripSeedTrial,
  response: SeedAutoAssignmentResponse,
  stripes: SeedStripe[],
  products: SeedProduct[],
  unit: string,
  standard?: StripStandardProduct
): StripeAssignments => {
  getStripIdentities(trial, stripes);

  if (response.trial_plots.length !== trial.strip_design.strips.length) {
    throw new Error(
      "The suggested assignments do not match the saved strip design."
    );
  }

  const normalized: SavedSeedProductAssignment[] = [];

  response.trial_plots.forEach((group, plotGroupId) => {
    const seeds = group.properties?.seeds;
    const entries = seeds?.rates_and_dosages ?? [];

    if (entries.length === 0) {
      return;
    }

    if (entries.length !== 1) {
      throw new Error(
        "The auto-assignment response must contain one seed entry per strip."
      );
    }

    const entry = entries[0];

    if (
      entry.plot_cell_id !== undefined &&
      entry.plot_cell_id !== null &&
      entry.plot_cell_id !== 0
    ) {
      throw new Error("A suggested strip has an invalid cell reference.");
    }

    const rate = entry.rate;
    const sourceUnit = entry.measure_unit ?? seeds?.measure_unit;

    if (
      typeof rate !== "number" ||
      !Number.isFinite(rate) ||
      rate < 0 ||
      sourceUnit === undefined ||
      sourceUnit === null ||
      sourceUnit.trim() === ""
    ) {
      throw new Error("A suggested strip has an invalid rate or unit.");
    }

    const isStandard = entry.is_standard === true;
    const matches = products.filter(
      (product) => product.name === entry.variety
    );

    const explicitId =
      typeof entry.product_id === "string" && entry.product_id.trim() !== ""
        ? entry.product_id
        : undefined;

    const productId =
      explicitId ??
      (isStandard
        ? standard?.id
        : matches.length === 1
        ? matches[0].id
        : undefined);

    const product = products.find((item) => item.id === productId);

    const variety =
      entry.variety ?? (isStandard ? standard?.name : product?.name);

    if (
      productId === undefined ||
      variety === undefined ||
      variety.trim() === ""
    ) {
      throw new Error(
        "A suggested seed product could not be identified safely."
      );
    }

    normalized.push({
      product_id: productId,
      variety,
      measure_unit: sourceUnit,
      is_standard: isStandard,
      assignments: [
        {
          plot_group_id: plotGroupId,
          plot_cell_id: 0,
          rate,
          quantity: entry.quantity,
          color: entry.color ?? product?.color ?? "",
        },
      ],
    });
  });

  const assignments = hydrateStripSeedAssignments(
    {
      ...trial,
      plot_property_assignments: {
        seed_product_assignments: normalized,
      },
    },
    stripes,
    unit
  );

  if (
    stripes.length === 0 ||
    Object.keys(assignments).length !== stripes.length ||
    stripes.some(({ id }) => {
      const assignment = assignments[id];
      const product = products.find(
        (item) => item.id === assignment?.productId
      );

      return (
        assignment === undefined ||
        assignment.productId === "" ||
        assignment.rate === null ||
        !Number.isFinite(assignment.rate) ||
        assignment.rate < 0 ||
        assignment.legacy === true ||
        (product === undefined && assignment.productId !== STRIP_STANDARD_OWNER)
      );
    })
  ) {
    throw new Error(
      "Generated assignments must cover every strip with a valid product and rate."
    );
  }

  return assignments;
};

export const buildStripSeedPayload = (
  trial: StripSeedTrial,
  stripes: SeedStripe[],
  before: StripeAssignments,
  after: StripeAssignments,
  products: SeedProduct[],
  unit: string,
  explicitlySaved: string[],
  standard?: StripStandardProduct,
  options: { replaceAllAssignments?: boolean } = {}
): SavedSeedProductAssignment[] => {
  const identities = getStripIdentities(trial, stripes);
  const validIds = new Set(stripes.map((stripe) => stripe.id));
  const replaceAllAssignments = options.replaceAllAssignments === true;

  if (
    [...Object.keys(before), ...Object.keys(after)].some(
      (id) => !validIds.has(id)
    )
  ) {
    throw new Error("The assignment draft contains an unknown strip.");
  }

  const affected = new Set([
    ...explicitlySaved,
    ...Object.values(after)
      .filter((assignment) => assignment.legacy !== true)
      .map((assignment) => assignment.productId),
  ]);

  stripes.forEach(({ id }) => {
    const previous = before[id];
    const current = after[id];

    if (JSON.stringify(previous) === JSON.stringify(current)) {
      return;
    }

    if (previous?.legacy === true && !replaceAllAssignments) {
      throw new Error(
        "Legacy assignments without product_id cannot be changed safely."
      );
    }

    if (previous !== undefined) affected.add(previous.productId);
    if (current !== undefined) affected.add(current.productId);
  });

  const stored =
    trial.plot_property_assignments?.seed_product_assignments ?? [];

  if (
    replaceAllAssignments &&
    Object.values(after).some((assignment) => assignment.legacy === true)
  ) {
    throw new Error(
      "Every generated strip must have a recognized seed product before saving."
    );
  }

  const result: SavedSeedProductAssignment[] = replaceAllAssignments
    ? []
    : stored
        .filter((product) => product.product_id === null)
        .map((product) => ({
          ...product,
          assignments: product.assignments.map((assignment) => ({
            ...assignment,
          })),
        }));
  const emitted = new Set<string>();
  const normalizedUnit = normalizeSeedUnit(unit);
  const unitMatch = /^(seeds|kg)\/(m2|ha)$/.exec(normalizedUnit);

  if (unitMatch === null) {
    throw new Error(`Unsupported seed measure unit: ${unit}.`);
  }

  const colorsByStripeId = getStripSeedColors(after, products, unit);

  affected.forEach((ownerId) => {
    const owned = identities.filter(
      ({ stripe }) => after[stripe.id]?.productId === ownerId
    );

    if (owned.length === 0) {
      const previousOwned = stripes.filter(
        ({ id }) => before[id]?.productId === ownerId
      );

      const entirelyReassigned =
        previousOwned.length > 0 &&
        previousOwned.every(({ id }) => {
          const current = after[id];

          return (
            current !== undefined &&
            current.productId !== "" &&
            current.productId !== ownerId
          );
        });

      if (entirelyReassigned) return;

      throw new Error(
        "The API cannot clear a product's final strip without reassignment. " +
          "Keep one strip selected or move its strips to another product."
      );
    }

    if (ownerId.startsWith("legacy:")) return;

    const isStandard = ownerId === STRIP_STANDARD_OWNER;
    const savedStandard = stored.find((product) => product.is_standard);

    const productId = isStandard
      ? standard?.id ?? savedStandard?.product_id
      : ownerId;

    const protocolProduct = products.find((product) => product.id === ownerId);

    const savedProduct = stored.find(
      (product) => product.product_id === productId
    );

    const variety = isStandard
      ? standard?.name ?? savedStandard?.variety
      : protocolProduct?.name ?? savedProduct?.variety;

    if (
      productId === undefined ||
      productId === null ||
      productId.trim() === "" ||
      productId.startsWith("legacy:") ||
      variety === undefined ||
      variety.trim() === ""
    ) {
      throw new Error("The seed product could not be identified.");
    }

    if (emitted.has(productId)) {
      throw new Error(
        "A product cannot be saved in two standard/non-standard roles."
      );
    }

    const standardProductId = standard?.id ?? savedStandard?.product_id;

    if (
      Object.values(after).some(
        (assignment) =>
          (isStandard && assignment.productId === productId) ||
          (!isStandard &&
            standardProductId === productId &&
            assignment.productId === STRIP_STANDARD_OWNER)
      )
    ) {
      throw new Error(
        "The API supports one standard flag per product. " +
          "The same product cannot occupy both roles."
      );
    }

    emitted.add(productId);

    const totals: NonNullable<SeedProductAssignment["plot_group_totals"]> = [];

    const assignments = owned.map(({ stripe, plotGroupId }) => {
      const assignment = after[stripe.id];
      const rate = assignment.rate;

      if (
        assignment.legacy === true ||
        typeof rate !== "number" ||
        !Number.isFinite(rate) ||
        rate < 0
      ) {
        throw new Error("Every selected strip must have a valid rate.");
      }

      const areaM2 = trial.strip_design.strips[plotGroupId].area;
      const quantity = calculateStripSeedQuantity(rate, areaM2, unit);

      const replicateMultiplier = getStripSeedReplicantMultiplier(
        trial.trial_plots?.[plotGroupId]?.replicants
      );

      const totalQuantity = quantity * replicateMultiplier;

      if (!Number.isFinite(totalQuantity) || totalQuantity < 0) {
        throw new Error("A calculated seed total is invalid.");
      }

      totals.push({
        plot_group_id: plotGroupId,
        value: totalQuantity,
        measure_unit: unitMatch[1],
      });

      return {
        plot_group_id: plotGroupId,
        plot_cell_id: 0,
        rate,
        quantity,
        color:
          getNonEmptySeedColor(colorsByStripeId[stripe.id], assignment.color) ??
          "",
      };
    });

    result.push({
      product_id: productId,
      variety,
      measure_unit: unit,
      is_standard: isStandard,
      assignments,
      plot_group_totals: totals,
    });
  });

  if (result.length === 0) {
    throw new Error("There are no assignments to save.");
  }

  return result;
};

export const reconcileStripSeedResponse = (
  trial: StripSeedTrial,
  response: SeedAssignmentsResponse
): StripSeedTrial =>
  validateStripSeedTrial({
    ...trial,
    plot_property_assignments: response,
  });
